import * as React from "react";
import { Box, CPText } from "../../../shared/components/base";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
  StackLayout,
} from "@progress/kendo-react-layout";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import type { CPTextBoxChangeEvent } from "shared/components/shared/CPTextBox/CPTextBox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import type { CPDropDownListEventChangeEvent } from "shared/components/shared/CPDropDownList/CPDropDownList";
import { useSsoRegisterMutation } from "data/auth/auth";
import logo from "../../../assets/evans-bi-logo2.png";
import "../RequestAccessPage/RequestAccessPageStyles.scss";
import { useNavigate, useSearchParams } from "react-router-dom";

const REQUEST_TYPES = ["Agent", "Employee"];

const agencyRequiresSelection = (requestType: string) =>
  requestType === "Agent";

interface FormState {
  requestType: string;
  agencyCode: string;
  externalSystemId: string;
}

const initialForm: FormState = {
  requestType: "",
  agencyCode: "",
  externalSystemId: "",
};

interface FormErrors {
  requestType?: string;
  agencyCode?: string;
}

const SsoRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = React.useState<FormState>(initialForm);
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [submitted, setSubmitted] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  // Display data and session token come from URL params set by the SSO callback redirect
  const firstname = searchParams.get("fn") ?? "";
  const lastname = searchParams.get("ln") ?? "";
  const email = searchParams.get("email") ?? "";
  const sessionId = searchParams.get("sid") ?? "";

  // If URL params are missing this page was reached without going through SSO
  React.useEffect(() => {
    if (!firstname || !lastname || !email || !sessionId) {
      navigate("/", { replace: true });
    }
  }, [firstname, lastname, email, sessionId, navigate]);

  const { mutate: register, isLoading: submitting } = useSsoRegisterMutation();

  const handleTextChange = (e: CPTextBoxChangeEvent) => {
    setForm((prev) => ({ ...prev, [e.name]: e.value as string }));
    setErrors((prev) => ({ ...prev, [e.name]: undefined }));
  };

  const handleRequestTypeChange = (e: CPDropDownListEventChangeEvent) => {
    setForm((prev) => ({
      ...prev,
      requestType: e.value as string,
      agencyCode: "",
    }));
    setErrors((prev) => ({
      ...prev,
      requestType: undefined,
      agencyCode: undefined,
    }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.requestType) newErrors.requestType = "Request type is required.";
    if (agencyRequiresSelection(form.requestType) && !form.agencyCode.trim())
      newErrors.agencyCode = "Agency code is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    register(
      {
        sessionId,
        requestType: form.requestType,
        agencyCode: agencyRequiresSelection(form.requestType)
          ? form.agencyCode.trim() || undefined
          : undefined,
        externalSystemId: form.externalSystemId.trim() || undefined,
      },
      {
        onSuccess: () => {
          setErrorMsg(null);
          setSubmitted(true);
        },
        onError: (error: unknown) => {
          const errCode = (error as Record<string, string>)?.error;
          if (errCode === "no_sso_session") {
            setErrorMsg(
              "Your registration session has expired. Please sign in with Microsoft again to restart.",
            );
            return;
          }
          if (errCode === "already_pending") {
            setErrorMsg(
              "A request already exists for this account. Please wait for an administrator to review it.",
            );
            return;
          }
          setErrorMsg(
            "An error occurred while submitting your request. Please try again.",
          );
        },
      },
    );
  };

  if (!firstname || !lastname || !email || !sessionId) {
    return null;
  }

  if (submitted) {
    return (
      <Card className="request-access-card">
        <CardHeader>
          <Box
            alignItems="center"
            justifyContent="center"
            className="logo-container"
          >
            <img
              src={logo}
              alt="BI Hub Logo"
              className="login-logo"
              width="250"
            />
          </Box>
          <CardTitle />
        </CardHeader>
        <CardBody>
          <StackLayout
            orientation="vertical"
            gap={16}
            align={{ horizontal: "center" }}
          >
            <CPText textStyle="h5" align="center">
              Request Submitted
            </CPText>
            <CPText textStyle="subtitle" align="center">
              Your access request has been received. An administrator will
              review it and finish setting up your account.
            </CPText>
            <CPText textStyle="subtitle" align="center">
              Once your account is approved, you will receive a confirmation
              email at <strong>{email}</strong>. You can then sign in with
              Microsoft.
            </CPText>
          </StackLayout>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="request-access-card">
      <CardHeader>
        <Box
          alignItems="center"
          justifyContent="center"
          className="logo-container"
        >
          <img
            src={logo}
            alt="BI Hub Logo"
            className="login-logo"
            width="250"
          />
        </Box>
        <CardTitle />
        <CardSubtitle>
          <CPText textStyle="subtitle" align="center">
            Complete your access request for the Evans Business Intelligence Hub
          </CPText>
        </CardSubtitle>
      </CardHeader>

      <CardBody>
        {errorMsg && (
          <CPText color="error" style={{ marginBottom: 12 }}>
            {errorMsg}
          </CPText>
        )}
        <StackLayout orientation="vertical" gap={16}>
          <StackLayout orientation="horizontal" gap={12}>
            <div style={{ flex: 1 }}>
              <CPTextBox
                name="firstname"
                label="First Name"
                value={firstname}
                disabled
              />
            </div>
            <div style={{ flex: 1 }}>
              <CPTextBox
                name="lastname"
                label="Last Name"
                value={lastname}
                disabled
              />
            </div>
          </StackLayout>

          <CPTextBox
            name="email"
            label="Email Address"
            value={email}
            disabled
          />

          <CPDropDownList
            name="requestType"
            label="Request Type"
            required
            data={REQUEST_TYPES}
            value={form.requestType || null}
            errorMsg={errors.requestType}
            onChange={handleRequestTypeChange}
          />

          {agencyRequiresSelection(form.requestType) && (
            <CPTextBox
              name="agencyCode"
              label="Primary Agency Code"
              required
              value={form.agencyCode}
              errorMsg={errors.agencyCode}
              onChange={handleTextChange}
            />
          )}

          <CPTextBox
            name="externalSystemId"
            label="External System Username"
            value={form.externalSystemId}
            onChange={handleTextChange}
            helpText="Enter your current username if you have one on platforms like GreatEdge, EBH, etc."
          />
        </StackLayout>
      </CardBody>

      <CardFooter>
        <StackLayout orientation="horizontal" gap={16}>
          <CPButton
            themeColor="primary"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </CPButton>
        </StackLayout>
      </CardFooter>
    </Card>
  );
};

export default SsoRegisterPage;
